import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { RefreshIcon, CloseIcon, CopyIcon, LinkIcon } from './Icons';
import Pagination from './Pagination';
import { COUNTRIES, DEFAULT_COUNTRY_ISO2, buildE164Contact, countryForNumber } from './countryCodes';
import { CURRENCIES, DEFAULT_CURRENCY } from './currencies';

const DEFAULT_PAGE_SIZE = 50;
// Razorpay is the only system of record for a link's status (Created, Paid,
// etc.) — nothing about it lives in this app's own DB (see
// server/ivr/razorpayClient.js) — so poll the same way DailyOrders.js/
// AbandonedCarts.js poll their own live sources, rather than requiring a
// manual refresh to notice a customer just paid.
const AUTO_REFRESH_MS = 10000;

const STATUS_LABELS = {
  created: 'Created',
  paid: 'Paid',
  partially_paid: 'Partially Paid',
  cancelled: 'Cancelled',
  expired: 'Expired',
};

// Reuses the existing `.status` pill classes (App.css) rather than inventing
// new ones — 'answered'/'missed'/'attempted'/'viewing' already cover the
// green/red/orange/blue tones this needs.
const STATUS_CLASSES = {
  created: 'viewing',
  paid: 'answered',
  partially_paid: 'attempted',
  cancelled: 'missed',
  expired: 'missed',
};

// Splits a full international number ("+919876543210") into the form's
// country picker + local-number fields, for prefilling from a WhatsApp chat.
const splitPhone = (fullNumber) => {
  const digits = String(fullNumber || '').replace(/\D/g, '');
  if (!digits) return { iso2: DEFAULT_COUNTRY_ISO2, local: '' };
  const country = countryForNumber(digits);
  // countryForNumber's NANP result is a combined label with no iso2.
  const iso2 = country?.iso2 || (digits.startsWith('1') ? 'US' : DEFAULT_COUNTRY_ISO2);
  const dialCode = (COUNTRIES.find((c) => c.iso2 === iso2) || {}).dialCode || '';
  return { iso2, local: dialCode && digits.startsWith(dialCode) ? digits.slice(dialCode.length) : digits };
};

export const formatAmount = (paise, currency) => {
  const value = Number(paise) / 100;
  if (Number.isNaN(value)) return '-';
  const formatted = value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return currency && currency !== 'INR' ? `${formatted} ${currency}` : `₹${formatted}`;
};

const formatDateTime = (unixSeconds) => {
  if (!unixSeconds) return '-';
  return new Date(unixSeconds * 1000).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Local <input type="datetime-local"> value -> unix seconds for Razorpay's
// expire_by field.
const toUnixSeconds = (datetimeLocalValue) => {
  if (!datetimeLocalValue) return null;
  const parsed = new Date(datetimeLocalValue);
  return Number.isNaN(parsed.getTime()) ? null : Math.floor(parsed.getTime() / 1000);
};

// The "Standard Payment Link" create form, matching the fields on Razorpay's
// own dashboard modal (amount, description, customer email/phone with
// per-channel notify toggles, reference id, expiry, partial payment, notes) —
// then hands back the created link's short_url so it can be copied/sent
// without leaving this modal.
//
// Also opened from the WhatsApp chat (WhatsAppChat.js): `initialPhone`
// prefills the customer's number, and `successActions(link)` renders extra
// buttons on the success screen (e.g. "Send in chat").
export function CreatePaymentLinkModal({ onClose, onCreated, initialPhone = '', successActions = null }) {
  const initialSplit = useMemo(() => splitPhone(initialPhone), [initialPhone]);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [countryIso2, setCountryIso2] = useState(initialSplit.iso2);
  const [phone, setPhone] = useState(initialSplit.local);
  const [notifySms, setNotifySms] = useState(false);
  const [referenceId, setReferenceId] = useState('');
  const [noExpiry, setNoExpiry] = useState(true);
  const [expireAt, setExpireAt] = useState('');
  const [acceptPartial, setAcceptPartial] = useState(false);
  const [firstMinPartialAmount, setFirstMinPartialAmount] = useState('');
  const [noteRows, setNoteRows] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const selectedCountry = COUNTRIES.find((c) => c.iso2 === countryIso2) || COUNTRIES[0];

  const addNoteRow = () => setNoteRows((prev) => [...prev, { key: '', value: '' }]);
  const updateNoteRow = (index, field, value) =>
    setNoteRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  const removeNoteRow = (index) => setNoteRows((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const contact = buildE164Contact(selectedCountry.dialCode, phone);
    if (!amount || Number(amount) <= 0) {
      setError('Enter a valid amount.');
      return;
    }
    if (!contact) {
      setError('Enter a valid customer phone number.');
      return;
    }
    if (acceptPartial && (!firstMinPartialAmount || Number(firstMinPartialAmount) <= 0)) {
      setError('Enter a minimum partial amount, or turn off partial payment.');
      return;
    }

    const notes = Object.fromEntries(
      noteRows.filter((row) => row.key.trim()).map((row) => [row.key.trim(), row.value])
    );

    setSubmitting(true);
    try {
      const link = await onCreated({
        amount: Number(amount),
        currency,
        description: description.trim() || undefined,
        customerEmail: email.trim() || undefined,
        customerContact: contact,
        notifyEmail,
        notifySms,
        referenceId: referenceId.trim() || undefined,
        expireBy: noExpiry ? undefined : toUnixSeconds(expireAt) || undefined,
        acceptPartial,
        firstMinPartialAmount: acceptPartial ? Number(firstMinPartialAmount) : undefined,
        notes,
      });
      setCreatedLink(link);
    } catch (err) {
      setError(err.message || 'Failed to create payment link.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!createdLink?.short_url) return;
    try {
      await navigator.clipboard.writeText(createdLink.short_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Clipboard access can be denied by the browser — the link is still
      // shown on screen for a manual copy either way.
    }
  };

  return (
    <div className="modal-overlay modal-overlay-center" onClick={onClose}>
      <div className="modal-panel modal-panel-compose" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Standard Payment Link</h2>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <CloseIcon width={18} height={18} />
          </button>
        </div>

        {createdLink ? (
          <div className="modal-body">
            <p>Payment link created.</p>
            <label className="login-field">
              <span>Shareable link</span>
              <div className="login-field-phone-row">
                <input type="text" value={createdLink.short_url || ''} readOnly />
                <button type="button" className="btn btn-secondary" onClick={handleCopy}>
                  <CopyIcon width={14} height={14} />
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </label>
            {successActions ? (
              successActions(createdLink)
            ) : (
              <button type="button" className="btn btn-primary login-submit" onClick={onClose}>
                Done
              </button>
            )}
          </div>
        ) : (
          <form className="modal-body" onSubmit={handleSubmit}>
            <label className="login-field">
              <span>Amount *</span>
              <div className="login-field-phone-row">
                <select value={currency} onChange={(event) => setCurrency(event.target.value)}>
                  {CURRENCIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  autoFocus
                  required
                />
              </div>
            </label>

            <label className="login-field">
              <span>Payment For</span>
              <input
                type="text"
                placeholder="Payment description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </label>

            <label className="login-field">
              <span>Customer email</span>
              <input
                type="email"
                placeholder="john@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label className="alerts-toggle">
              <input type="checkbox" checked={notifyEmail} onChange={(event) => setNotifyEmail(event.target.checked)} />
              <span>Notify via Email</span>
            </label>

            <label className="login-field">
              <span>Customer phone *</span>
              <div className="login-field-phone-row">
                <select value={countryIso2} onChange={(event) => setCountryIso2(event.target.value)}>
                  {COUNTRIES.map((country) => (
                    <option key={country.iso2} value={country.iso2}>
                      {country.flag} {country.name} (+{country.dialCode})
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  placeholder="1234567890"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  required
                />
              </div>
            </label>
            <label className="alerts-toggle">
              <input type="checkbox" checked={notifySms} onChange={(event) => setNotifySms(event.target.checked)} />
              <span>Notify via SMS</span>
            </label>

            <label className="login-field">
              <span>Reference Id</span>
              <input
                type="text"
                placeholder="123456"
                value={referenceId}
                onChange={(event) => setReferenceId(event.target.value)}
              />
            </label>

            <label className="alerts-toggle">
              <input type="checkbox" checked={noExpiry} onChange={(event) => setNoExpiry(event.target.checked)} />
              <span>No Expiry</span>
            </label>
            {!noExpiry && (
              <label className="login-field">
                <span>Expires At</span>
                <input type="datetime-local" value={expireAt} onChange={(event) => setExpireAt(event.target.value)} />
              </label>
            )}

            <label className="alerts-toggle">
              <input
                type="checkbox"
                checked={acceptPartial}
                onChange={(event) => setAcceptPartial(event.target.checked)}
              />
              <span>Enable Partial Payment</span>
            </label>
            {acceptPartial && (
              <label className="login-field">
                <span>Minimum partial amount ({currency})</span>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={firstMinPartialAmount}
                  onChange={(event) => setFirstMinPartialAmount(event.target.value)}
                />
              </label>
            )}

            <div className="login-field">
              <span>Notes</span>
              {noteRows.map((row, index) => (
                <div className="login-field-phone-row" key={index}>
                  <input
                    type="text"
                    placeholder="Key"
                    value={row.key}
                    onChange={(event) => updateNoteRow(index, 'key', event.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Value"
                    value={row.value}
                    onChange={(event) => updateNoteRow(index, 'value', event.target.value)}
                  />
                  <button type="button" className="btn btn-secondary btn-small" onClick={() => removeNoteRow(index)}>
                    <CloseIcon width={12} height={12} />
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-secondary btn-small" onClick={addNoteRow}>
                + Add New
              </button>
            </div>

            {error && <div className="login-error">{error}</div>}
            <button type="submit" className="btn btn-primary login-submit" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create Payment Link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// Create-and-track view for Razorpay Standard Payment Links — a brand-new
// integration (see CLAUDE.md's Payment Links section), separate from the
// ERP/Shopify integrations elsewhere in this app. Every link and its status
// is fetched fresh from Razorpay on load and on each poll; nothing about a
// link is stored in this app's own DB.
function PaymentLinks() {
  const { authFetch } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchLinks = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await authFetch('/api/ivr/payment-links');
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        if (!silent) setLinks([]);
        setError(body?.error || 'Failed to fetch payment links.');
        return;
      }
      setError('');
      setLinks(Array.isArray(body?.links) ? body.links : []);
    } catch (err) {
      if (!silent) setLinks([]);
      setError(err.message || 'Failed to fetch payment links.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinks();
    const interval = setInterval(() => fetchLinks({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  const statusCounts = useMemo(() => {
    const counts = { all: links.length, created: 0, paid: 0, partially_paid: 0, cancelled: 0, expired: 0 };
    links.forEach((link) => {
      if (counts[link.status] !== undefined) counts[link.status] += 1;
    });
    return counts;
  }, [links]);

  const filteredLinks = useMemo(() => {
    const term = search.trim().toLowerCase();
    return links.filter((link) => {
      if (statusFilter !== 'all' && link.status !== statusFilter) return false;
      if (!term) return true;
      const haystack = [
        link.description,
        link.reference_id,
        link.short_url,
        link.customer?.name,
        link.customer?.email,
        link.customer?.contact,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [links, statusFilter, search]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filteredLinks.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const pageRows = filteredLinks.slice((page - 1) * pageSize, page * pageSize);

  const handleCreate = async (payload) => {
    const response = await authFetch('/api/ivr/payment-links', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = await response.json().catch(() => null);
    if (!response.ok) throw new Error(body?.error || 'Failed to create payment link.');
    await fetchLinks({ silent: true });
    return body.link;
  };

  const handleCopyRow = async (shortUrl) => {
    try {
      await navigator.clipboard.writeText(shortUrl);
    } catch (err) {
      // Non-critical - the URL is already visible in the row for manual copy.
    }
  };

  return (
    <>
      <div className="incoming-filters-bar">
        <button type="button" className="btn btn-secondary" onClick={() => fetchLinks()} disabled={loading}>
          <RefreshIcon width={14} height={14} />
          Refresh
        </button>
        <input
          type="text"
          placeholder="Search by customer, description, reference id…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="search-input"
        />
        <button type="button" className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <LinkIcon width={14} height={14} />
          Create Payment Link
        </button>
      </div>

      <div className="stat-cards">
        <button type="button" className={`stat-card ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => setStatusFilter('all')}>
          <div className="stat-value">{statusCounts.all}</div>
          <div className="stat-label">All</div>
        </button>
        <button type="button" className={`stat-card ${statusFilter === 'created' ? 'active' : ''}`} onClick={() => setStatusFilter('created')}>
          <div className="stat-value">{statusCounts.created}</div>
          <div className="stat-label">Created</div>
        </button>
        <button type="button" className={`stat-card answered ${statusFilter === 'paid' ? 'active' : ''}`} onClick={() => setStatusFilter('paid')}>
          <div className="stat-value">{statusCounts.paid}</div>
          <div className="stat-label">Paid</div>
        </button>
        <button type="button" className={`stat-card ${statusFilter === 'partially_paid' ? 'active' : ''}`} onClick={() => setStatusFilter('partially_paid')}>
          <div className="stat-value">{statusCounts.partially_paid}</div>
          <div className="stat-label">Partially Paid</div>
        </button>
        <button type="button" className={`stat-card critical ${statusFilter === 'cancelled' ? 'active' : ''}`} onClick={() => setStatusFilter('cancelled')}>
          <div className="stat-value">{statusCounts.cancelled}</div>
          <div className="stat-label">Cancelled</div>
        </button>
        <button type="button" className={`stat-card critical ${statusFilter === 'expired' ? 'active' : ''}`} onClick={() => setStatusFilter('expired')}>
          <div className="stat-value">{statusCounts.expired}</div>
          <div className="stat-label">Expired</div>
        </button>
      </div>

      {error && <div className="call-feedback call-feedback-error">{error}</div>}

      {loading ? (
        <div className="loading-state">Loading…</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Created At</th>
                <th>Amount</th>
                <th>Payment For</th>
                <th>Customer</th>
                <th>Reference Id</th>
                <th>Payment Link</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center' }}>
                    {error ? 'Could not load payment links.' : 'No payment links match this filter.'}
                  </td>
                </tr>
              ) : (
                pageRows.map((link) => (
                  <tr key={link.id}>
                    <td>{formatDateTime(link.created_at)}</td>
                    <td>{formatAmount(link.amount, link.currency)}</td>
                    <td>{link.description || '-'}</td>
                    <td>{link.customer?.name || link.customer?.contact || link.customer?.email || '-'}</td>
                    <td>{link.reference_id || '-'}</td>
                    <td>
                      <div className="login-field-phone-row">
                        <a href={link.short_url} target="_blank" rel="noreferrer">
                          {link.short_url}
                        </a>
                        <button type="button" className="btn btn-secondary btn-small" onClick={() => handleCopyRow(link.short_url)}>
                          <CopyIcon width={12} height={12} />
                        </button>
                      </div>
                    </td>
                    <td>
                      <span className={`status ${STATUS_CLASSES[link.status] || 'attempted'}`}>
                        {STATUS_LABELS[link.status] || link.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        totalItems={filteredLinks.length}
      />

      {showCreateModal && (
        <CreatePaymentLinkModal onClose={() => setShowCreateModal(false)} onCreated={handleCreate} />
      )}
    </>
  );
}

export default PaymentLinks;
